import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Instagram } from 'lucide-react';
import { toast } from 'sonner';

export function BasicInfoStep({ form, setForm, usernameStatus }) {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">Tell us about yourself</h2>
        <p className="text-stone-500">This information will be displayed on your public profile.</p>
      </div>

      <div className="space-y-4">
        {/* Profile Picture */}
        <div className="flex items-center gap-4" data-testid="onboarding-profile-picture">
          <div className="relative group">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-[#F9F6F0] flex items-center justify-center border-2 border-[#E6E4DD]">
              {form.profile_image ? (
                <img src={form.profile_image} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl text-[#D4AF37] font-semibold">{form.brand_name?.charAt(0)?.toUpperCase() || '?'}</span>
              )}
            </div>
            <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
              <span className="text-white text-xs font-medium">Upload</span>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
                  setForm({...form, profile_image: URL.createObjectURL(file), _profileFile: file });
                }
              }} data-testid="onboarding-profile-upload" />
            </label>
          </div>
          <div>
            <Label>Profile Picture</Label>
            <p className="text-xs text-stone-400">Recommended: square image, at least 200x200px</p>
          </div>
        </div>

        <div>
          <Label htmlFor="brand_name">Business / Brand Name *</Label>
          <Input
            id="brand_name"
            value={form.brand_name}
            onChange={(e) => setForm({...form, brand_name: e.target.value})}
            placeholder="Your business name"
            className="input-zuriya"
            data-testid="brand-name-input"
          />
        </div>

        <div>
          <Label htmlFor="username">Your Zuriya Link *</Label>
          <div className="flex items-center gap-0 mt-1">
            <span className="px-3 py-2 bg-[#F9F6F0] border border-r-0 border-[#E6E4DD] rounded-l-lg text-sm text-stone-500 whitespace-nowrap font-mono">
              zuriya.co.uk/@
            </span>
            <Input
              id="username"
              value={form.username}
              onChange={(e) => setForm({...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, '').replace(/^@/, '')})}
              placeholder="jasmine"
              className="input-zuriya rounded-l-none font-mono lowercase"
              maxLength={30}
              data-testid="username-input"
            />
          </div>
          <div className="mt-1.5 text-xs">
            {usernameStatus === 'checking' && <span className="text-stone-400">Checking availability...</span>}
            {usernameStatus === 'available' && <span className="text-green-600">zuriya.co.uk/@{form.username.replace(/^@/, '')} is available</span>}
            {usernameStatus === 'taken' && <span className="text-red-500">This username is already taken</span>}
            {usernameStatus === 'invalid' && <span className="text-red-500">Min 3 characters, letters, numbers, dots and underscores only</span>}
            {usernameStatus === null && form.username.length === 0 && <span className="text-stone-400">This will be your shareable profile link</span>}
          </div>
        </div>

        <div>
          <Label htmlFor="bio">Bio *</Label>
          <Textarea
            id="bio"
            value={form.bio}
            onChange={(e) => setForm({...form, bio: e.target.value})}
            placeholder="Tell clients about yourself and your services..."
            className="input-zuriya min-h-[120px]"
            data-testid="bio-input"
          />
        </div>

        <div className="pt-4 border-t border-[#E6E4DD]">
          <Label className="text-base font-medium text-[#1A1A1A]">Your Address *</Label>
          <p className="text-xs text-stone-500 mb-3">Your full address is stored securely and never shown publicly. Clients only see your city and postcode area (e.g. "Brixton, SW9").</p>
          <div className="space-y-3">
            <div>
              <Label htmlFor="address-line1" className="text-sm">House/Flat Number & Street *</Label>
              <Input
                id="address-line1"
                value={form.full_address?.line1 || ''}
                onChange={(e) => setForm({...form, full_address: {...(form.full_address || {}), line1: e.target.value}})}
                placeholder="e.g. 12 High Street"
                className="input-zuriya"
                data-testid="address-line1-input"
              />
            </div>
            <div>
              <Label htmlFor="address-line2" className="text-sm">Flat/Unit (optional)</Label>
              <Input
                id="address-line2"
                value={form.full_address?.line2 || ''}
                onChange={(e) => setForm({...form, full_address: {...(form.full_address || {}), line2: e.target.value}})}
                placeholder="e.g. Flat 3B"
                className="input-zuriya"
                data-testid="address-line2-input"
              />
            </div>
            <div>
              <Label htmlFor="address-city" className="text-sm">City/Town *</Label>
              <Input
                id="address-city"
                value={form.full_address?.city || ''}
                onChange={(e) => setForm({...form, full_address: {...(form.full_address || {}), city: e.target.value}, location: e.target.value})}
                placeholder="e.g. Brixton"
                className="input-zuriya"
                data-testid="address-city-input"
              />
            </div>
            <div>
              <Label htmlFor="address-postcode" className="text-sm">Postcode *</Label>
              <Input
                id="address-postcode"
                value={form.postcode || ''}
                onChange={(e) => {
                  const pc = e.target.value.toUpperCase();
                  setForm({...form, postcode: pc, full_address: {...(form.full_address || {}), postcode: pc}});
                }}
                placeholder="e.g. SW9 8LQ"
                className="input-zuriya font-mono uppercase max-w-[200px]"
                maxLength={10}
                data-testid="postcode-input"
              />
              <p className="text-xs text-stone-400 mt-1">Used for verification and travel distance calculations</p>
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="pt-4 border-t border-[#E6E4DD]" data-testid="social-links-section">
          <Label className="text-base font-medium text-[#1A1A1A]">Social Media *</Label>
          <p className="text-xs text-stone-500 mb-3">Required for verification purposes only.</p>
          <div className="space-y-3">
            <div className="flex items-center gap-0">
              <span className="flex items-center gap-1.5 px-3 py-2 bg-[#F9F6F0] border border-r-0 border-[#E6E4DD] rounded-l-lg text-sm text-stone-500 whitespace-nowrap">
                <Instagram className="h-3.5 w-3.5" />
                instagram.com/
              </span>
              <Input
                value={form.social_links.instagram || ''}
                onChange={(e) => setForm({...form, social_links: {...form.social_links, instagram: e.target.value.replace(/^@/, '').trim()}})}
                placeholder="yourhandle"
                className="input-zuriya rounded-l-none"
                data-testid="social-instagram-input"
              />
            </div>
            <div className="flex items-center gap-0">
              <span className="flex items-center gap-1.5 px-3 py-2 bg-[#F9F6F0] border border-r-0 border-[#E6E4DD] rounded-l-lg text-sm text-stone-500 whitespace-nowrap">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.28 0 .56.04.81.1v-3.5a6.37 6.37 0 00-.81-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52V6.81a4.84 4.84 0 01-1-.12z"/></svg>
                tiktok.com/@
              </span>
              <Input
                value={form.social_links.tiktok || ''}
                onChange={(e) => setForm({...form, social_links: {...form.social_links, tiktok: e.target.value.replace(/^@/, '').trim()}})}
                placeholder="yourhandle"
                className="input-zuriya rounded-l-none"
                data-testid="social-tiktok-input"
              />
            </div>
          </div>
          {!Object.values(form.social_links).some(v => v && v.trim()) && (
            <p className="text-xs text-red-500 mt-2">Please add at least one social media link</p>
          )}
        </div>
      </div>
    </div>
  );
}
